import { fetchJson } from './client';
import type { ProjectImportRow, ProjectImportResponse } from '../../../shared/types/projectImport';
import type { ClientImportRow, ClientImportResponse } from '../../../shared/types/clientImport';

export const admin = {
  stats: () => fetchJson<any>('/admin/stats'),

  // Clients
  getClients: () => fetchJson<any[]>('/admin/clients'),
  createClient: (data: any) => fetchJson('/admin/clients', { method: 'POST', body: JSON.stringify(data) }),
  getClient: (id: string) => fetchJson<any>(`/admin/clients/${id}`),
  updateClient: (id: string, data: any) => fetchJson(`/admin/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteClient: (id: string) => fetchJson(`/admin/clients/${id}`, { method: 'DELETE' }),
  bulkClientInfo: (ids: string[]) => fetchJson<Array<{ id: string; name: string; userCount: number; projectCount: number }>>('/admin/clients/bulk-info', { method: 'POST', body: JSON.stringify({ ids }) }),
  bulkDeleteClients: (ids: string[]) => fetchJson('/admin/clients/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  importClients: (rows: ClientImportRow[], dryRun: boolean) =>
    fetchJson<ClientImportResponse>('/admin/clients/import', { method: 'POST', body: JSON.stringify({ rows, dryRun }) }),

  // Users
  getUsers: () => fetchJson<any[]>('/admin/users'),
  createUser: (data: any) => fetchJson('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  getUser: (id: string) => fetchJson<any>(`/admin/users/${id}`),
  updateUser: (id: string, data: any) => fetchJson(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id: string) => fetchJson(`/admin/users/${id}`, { method: 'DELETE' }),
  bulkUserInfo: (ids: string[]) => fetchJson<Array<{ id: string; name: string | null; email: string; role: string; hasClerk: boolean; organizations: string[] }>>('/admin/users/bulk-info', { method: 'POST', body: JSON.stringify({ ids }) }),
  bulkDeleteUsers: (ids: string[]) => fetchJson('/admin/users/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  bulkCreateUsers: (users: Array<{ email: string; password: string; name?: string; role?: string; organization?: string; orgRole?: string }>) =>
    fetchJson<{ created: number; failed: number; results: Array<{ email: string; success: boolean; error?: string }> }>('/admin/users/bulk-create', { method: 'POST', body: JSON.stringify({ users }) }),

  // User profile photo
  uploadUserPhoto: (userId: string, photoData: string) =>
    fetchJson<{ profilePhotoPath: string }>(`/admin/users/${userId}/photo`, { method: 'POST', body: JSON.stringify({ photoData }) }),
  deleteUserPhoto: (userId: string) =>
    fetchJson(`/admin/users/${userId}/photo`, { method: 'DELETE' }),

  // Enrollment
  getEnrollmentSettings: (clientId: string) =>
    fetchJson<any>(`/admin/clients/${clientId}/enrollment`),
  updateEnrollmentSettings: (clientId: string, settings: any) =>
    fetchJson(`/admin/clients/${clientId}/enrollment`, { method: 'POST', body: JSON.stringify(settings) }),
  regenerateEnrollmentCode: (clientId: string) =>
    fetchJson<{ enrollmentCode: string }>(`/admin/clients/${clientId}/enrollment/regenerate-code`, { method: 'POST' }),
  getPendingUsers: (clientId: string) =>
    fetchJson<any[]>(`/admin/clients/${clientId}/pending-users`),
  approveUser: (clientId: string, userId: string) =>
    fetchJson(`/admin/clients/${clientId}/users/${userId}/approve`, { method: 'POST' }),
  rejectUser: (clientId: string, userId: string) =>
    fetchJson(`/admin/clients/${clientId}/users/${userId}/reject`, { method: 'POST' }),

  // Client membership (users under a client)
  addClientMember: (clientId: string, userId: string, role: string) =>
    fetchJson(`/admin/clients/${clientId}/members`, { method: 'POST', body: JSON.stringify({ userId, role }) }),
  updateClientMemberRole: (clientId: string, userId: string, role: string) =>
    fetchJson(`/admin/clients/${clientId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  removeClientMember: (clientId: string, userId: string) =>
    fetchJson(`/admin/clients/${clientId}/members/${userId}`, { method: 'DELETE' }),

  // Share links
  getShareLinks: (projectId?: string) =>
    fetchJson<any[]>(`/admin/share-links${projectId ? `?projectId=${projectId}` : ''}`),
  createShareLink: (data: any) =>
    fetchJson('/admin/share-links', { method: 'POST', body: JSON.stringify(data) }),
  updateShareLink: (id: string, data: any) =>
    fetchJson(`/admin/share-links/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteShareLink: (id: string) =>
    fetchJson(`/admin/share-links/${id}`, { method: 'DELETE' }),

  // Project assignment
  assignClient: (projectId: string, clientId: string | null) =>
    fetchJson(`/admin/projects/${projectId}/assign-client`, { method: 'PATCH', body: JSON.stringify({ clientId }) }),

  // Resolve coordinates for projects missing them (force = re-geocode every project with an address)
  geocodeProjects: (force = false) =>
    fetchJson<{ attempted: number; located: number; failed: number; failedIds: string[]; noAddress: number }>('/admin/projects/geocode', { method: 'POST', body: JSON.stringify({ force }) }),

  // Bulk project import (CSV). dryRun validates + resolves clients without inserting.
  importProjects: (rows: ProjectImportRow[], dryRun: boolean) =>
    fetchJson<ProjectImportResponse>('/admin/projects/import', { method: 'POST', body: JSON.stringify({ rows, dryRun }) }),

  // Client logo
  uploadClientLogo: (clientId: string, logoData: string) =>
    fetchJson<{ logoPath: string }>(`/admin/clients/${clientId}/logo`, { method: 'POST', body: JSON.stringify({ logoData }) }),
  deleteClientLogo: (clientId: string) =>
    fetchJson(`/admin/clients/${clientId}/logo`, { method: 'DELETE' }),
};
